import { Skeleton } from "@mui/material";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import {
  GalleryContainer,
  GalleryImage,
  GalleryItem,
  SkeletonItem,
} from "../components/StyledComponents";
import MedienDetails from "../DetailFunctions/MedienDetails";
import useFetchConfig from "../fetchFunction/ConfigFetch";
import Pagination from "../Pagination";
import "../styles/MedienOverview.css";

const useFetchPaginatedElements = (companyAccessname, page, pageSize) => {
  const [data, setData] = useState({
    image_urls: [],
    loading: true,
    error: null,
    totalPages: 0,
    pageSize: pageSize,
    page: page
  });

  useEffect(() => {
    const fetchData = async () => {
      setData((prevState) => ({ ...prevState, loading: true }));

      try {
        const response = await fetch(
          process.env.API_BASE_URL + `/pcw/${companyAccessname}/media/page/${page}/pageSize/${pageSize}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const json = await response.json();

        setData({
          image_urls: json.image_urls || [],
          loading: false,
          error: null,
          totalPages: json.meta?.totalPages || 0,
          pageSize: json.meta?.pageSize || pageSize,
        });
      } catch (error) {
        setData({ image_urls: [], loading: false, error, totalPages: 0, pageSize });
      }
    };

    fetchData();
  }, [companyAccessname, page, pageSize]);

  return {
    ...data,
    pageSize: data.pageSize || pageSize,
  };
};

const MedienOverview = ({ styling, companyAccessname }) => {
  const { configSettings } = useFetchConfig();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(24);

  const {
    image_urls: images,
    loading,
    error,
    totalPages,
    pageSize,
  } = useFetchPaginatedElements(companyAccessname, currentPage, itemsPerPage);

  useEffect(() => {
    if (pageSize && pageSize !== itemsPerPage) {
      setItemsPerPage(pageSize);
    }
  }, [pageSize]);

  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [selectedMedienData, setSelectedMedienData] = useState(null);

  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      window.scrollTo(0, 0);
    }
  };

  const handleMedienClick = (medien) => {
    setSelectedMedienData(medien);
    setIsOverlayOpen(true);
  };

  const closeOverlay = () => {
    setIsOverlayOpen(false);
    setSelectedMedienData(null);
  };

  return (
    <>
      <GalleryContainer>
        {loading ? (
          Array.from(new Array(itemsPerPage)).map((_, index) => (
            <GalleryItem key={index}>
              <SkeletonItem>
                <Skeleton variant="rectangular" width="100%" height="100%" />
              </SkeletonItem>
            </GalleryItem>
          ))
        ) : error ? (
          <p style={{ color: styling.textColor }}>Error: {error.message}</p>
        ) : !Array.isArray(images) || images.length === 0 ? (
          <p style={{ color: styling.textColor }}>Keine Daten verfügbar.</p>
        ) : (
          images.map((image) => (
            <GalleryItem
              key={image.id}
              onClick={() => handleMedienClick(image)}
            >
              <GalleryImage
                src={image.thumbnailUrl}
                alt={`Image ${image.id}`}
              />
            </GalleryItem>
          ))
        )}
      </GalleryContainer>

      {isOverlayOpen && selectedMedienData && (
        <div className="media-overlay">
          <MedienDetails
            medienData={selectedMedienData}
            configSettings={configSettings}
            onClose={closeOverlay}
          />
        </div>
      )}

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        styling={styling}
        onChangePage={goToPage}
      />
    </>
  );
};

MedienOverview.propTypes = {
  styling: PropTypes.shape({
    textColor: PropTypes.string,
    widgetButtonBackgroundColor: PropTypes.string,
    hoverButtonBackgroundColor: PropTypes.string,
    activeButtonBackgroundColor: PropTypes.string,
    activeButtonTextColor: PropTypes.string,
  }),
  companyAccessname: PropTypes.string.isRequired,
};

export default MedienOverview;
